import {
  useCreateAssistantThread,
  useSendAssistantMessage,
  useSelectAssistantThread,
} from '@/modules/assistant/hooks';
import type { AssistantScope } from '@/modules/assistant/services/assistant-scope';

export type AssistantSendState = {
  /** Sends one turn, opening a conversation first when there is none. */
  send: (text: string) => Promise<void>;
  /** True while a turn is in flight. */
  isSending: boolean;
  /** The failure of the last send, or null. */
  error: unknown;
};

/**
 * Sending one turn.
 *
 * ---------------------------------------------------------------------------
 * A CONVERSATION IS OPENED BEFORE THE FIRST TURN
 * ---------------------------------------------------------------------------
 * A turn belongs to a conversation, so a conversation is opened first when there
 * is none. The thread is opened ANCHORED TO THE ROUTE the turn happened on, which
 * is what lets the agent answer as the page's skill rather than as a generic
 * assistant.
 *
 * The person never sees two steps: opening the conversation and sending the turn
 * are one action, and a failure in either is reported the same way.
 */
export function useAssistantSend(
  scope: AssistantScope,
  route: string,
  threadId: string | null,
): AssistantSendState {
  const createThread = useCreateAssistantThread(scope);
  const sendMessage = useSendAssistantMessage(scope);
  const selectThread = useSelectAssistantThread();

  const openThread = async () => {
    const thread = await createThread.mutateAsync({ route });
    selectThread(thread.id, scope);
    return thread.id;
  };

  const send = async (text: string) => {
    const target = threadId ?? (await openThread());
    await sendMessage.mutateAsync({ threadId: target, message: text, route });
  };

  return {
    send,
    isSending: sendMessage.isPending || createThread.isPending,
    error: sendMessage.error ?? createThread.error,
  };
}
